/**
 * Student create form.
 * Prefills parent contact when opened from a parent, then POSTs registration + matching in one submit.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useListParentsRemote,
  useGetParentRemoteFullState,
  useCreateStudentRemoteProfile,
} from '@/hooks/useUsers';
import { useAuthStore } from '@/stores/authStore';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import { usePartnersOptions } from '@/hooks/usePartners';
import { getLevels } from '@/services/levels.service';
import { useProgramsForMode } from '@/hooks/useProgramsForMode';
import { studentRemoteCreateValidationSchema } from '@/utils/validation-schemas-utils';
import {
  areContactsIdentical,
  isPrimaryContactFormValid,
  useDefaultPhoneCountry,
} from '@/utils/user-contact-utils';
import { useFormValidation } from '@/hooks/useFormValidation';
import { resolveContactCheckType, useContactExistsCheck } from '@/hooks/useContactExistsCheck';
import type { ContactKind } from '@/types/user.types';
import { toast } from '@/components/ui/toast';
import { buildPendingDocumentFilePatch } from '@/utils/pending-document-link-utils';
import { hasPendingUploadFiles, uploadPendingFiles } from '@/utils/pending-upload-utils';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  REMOTE_SUBJECT_OPTIONS,
  STUDENT_FORM_QUERY_KEYS,
} from '@/components/students/student-configs';
import {
  isAdultFromDob,
  buildCreateInitialData,
  buildParentContactMap,
  buildParentOptions,
  buildLevelOptions,
  buildNamedOptions,
  studentHasSportProgram,
  studentValidationPayload,
  extractParentContactFromFullState,
  useStudentParentContactPrefill,
  useStudentParentAddressPrefill,
  applyNestedPath,
  applyStudentFormField,
  buildCreateStudentDto,
  defaultUpload,
  mergePendingUploads,
  isCreateStudentFormComplete,
  useSportClubOptions,
} from './student-form-utils';
import { StudentRegistrationSection } from './student-form-builder/StudentRegistrationSection';
import { StudentMatchingSection } from './student-form-builder/StudentMatchingSection';
import { StudentWelcomeMeetingSection } from './student-form-builder/StudentWelcomeMeetingSection';
import { StudentDocumentsSection } from './student-form-builder/StudentDocumentsSection';

/** Props for {@link CreateStudentView}. */
interface CreateStudentViewProps {
  onClose: () => void;
  onSuccess?: () => void;
  initialParentUserId?: string;
  initialParentName?: string;
  initialParentEmail?: string;
  initialParentPhone?: string;
}

/** Student create form. */
export function CreateStudentView({
  onClose,
  onSuccess,
  initialParentUserId,
  initialParentName,
  initialParentEmail,
  initialParentPhone,
}: Readonly<CreateStudentViewProps>) {
  const { user } = useAuthStore();
  const companyId = user?.companyId ?? '';
  const defaultCountry = useDefaultPhoneCountry();
  const validation = useFormValidation(studentRemoteCreateValidationSchema);

  const { data: levels = [] } = useQuery({
    queryKey: STUDENT_FORM_QUERY_KEYS.levels,
    queryFn: async () => (await getLevels({ pageSize: 10 })).data,
  });

  const { data: programsResponse } = useProgramsForMode({ statuses: ['active'] });
  const programs = programsResponse?.data ?? [];

  const { data: partners = [], error: partnersError } = usePartnersOptions();
  const { data: sportClubs = [], error: sportClubsError } = useSportClubOptions();

  const [parentSearch, setParentSearch] = React.useState('');
  const { data: parentResponse, isFetching: isParentFetching } = useListParentsRemote(
    { companyId, pageSize: 10, search: parentSearch || undefined },
    { enabled: !!companyId },
  );

  const parentOptions = React.useMemo(() => buildParentOptions(parentResponse), [parentResponse]);
  const parentContactMap = React.useMemo(
    () =>
      buildParentContactMap(
        parentResponse,
        initialParentUserId,
        initialParentEmail,
        initialParentPhone,
      ),
    [parentResponse, initialParentUserId, initialParentEmail, initialParentPhone],
  );
  const levelOptions = React.useMemo(() => buildLevelOptions(levels), [levels]);
  const partnerOptions = React.useMemo(() => buildNamedOptions(partners), [partners]);
  const sportClubOptions = React.useMemo(() => buildNamedOptions(sportClubs), [sportClubs]);

  const [formData, setFormData] = React.useState<Record<string, any>>(() =>
    buildCreateInitialData(initialParentUserId),
  );
  const [contactType, setContactType] = React.useState<ContactKind>('unknown');
  const [secondaryContact, setSecondaryContact] = React.useState('');
  const [pendingFiles, setPendingFiles] = React.useState<Record<string, File | null>>({});
  const [isUploading, setIsUploading] = React.useState(false);

  const isAdult = React.useMemo(() => isAdultFromDob(formData.dateOfBirth), [formData.dateOfBirth]);
  const activeParentUserId = formData.parentUserId ?? initialParentUserId ?? '';

  const { exists: contactExists, isChecking: isPrimaryChecking } = useContactExistsCheck({
    value: formData.contact || '',
    type: resolveContactCheckType(contactType),
    enabled: isAdult && contactType !== 'unknown' && (formData.contact || '').trim().length > 0,
    excludeUserId: activeParentUserId || undefined,
  });

  const contactsAreIdentical = areContactsIdentical(formData.contact || '', secondaryContact);

  const { data: parentFullState } = useGetParentRemoteFullState(activeParentUserId, companyId, {
    enabled: !!activeParentUserId && !!companyId,
  });

  const resolvedParentContact = extractParentContactFromFullState(parentFullState) ??
    parentContactMap[activeParentUserId] ?? {
      email: initialParentEmail,
      phone: initialParentPhone,
    };

  const hasSportProgram = studentHasSportProgram(programs, formData.programs);

  useStudentParentContactPrefill({
    isAdult,
    parentUserId: activeParentUserId,
    parentFullState,
    defaultCountry,
    parentContactMap,
    initialParentUserId,
    initialParentEmail,
    initialParentPhone,
    setFormData,
    setContactType,
    setSecondaryContact,
  });

  useStudentParentAddressPrefill({
    livingWithParents: formData.livingWithParents,
    parentUserId: activeParentUserId,
    parentFullState,
    setFormData,
  });

  React.useEffect(() => {
    validation.validateInitialFormData(
      studentValidationPayload(formData, isAdult, formData.contactType, secondaryContact),
    );
  }, []);

  const createMutation = useCreateStudentRemoteProfile();

  /** Set one top-level field and re-validate with adult / contact-type context. */
  const handleChange = (key: string, value: any) => {
    setFormData((prev) => applyStudentFormField(prev, key, value));
    validation.validateField(
      key,
      value,
      studentValidationPayload(
        { ...formData, [key]: value },
        isAdult,
        formData.contactType,
        secondaryContact,
      ),
    );
    validation.markAsTouched(key);
    validation.markAsDirty(key);
  };

  /** Mark a field touched so its error can show. */
  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  /** Patch a dotted path (used by documents nested objects). */
  const handleNestedChange = (path: string, value: any) => {
    setFormData((prev) => applyNestedPath(prev, path, value));
  };

  /** Store the file for submit upload and write a pending link on the school-report fields. */
  const handleFileChange = (key: string, file: File | null) => {
    setPendingFiles((prev) => ({ ...prev, [key]: file }));
    const patch = buildPendingDocumentFilePatch(key, file);
    if (!patch) return;
    setFormData((prev) => {
      let next = prev;
      Object.entries(patch).forEach(([path, value]) => {
        next = applyNestedPath(next, path, value);
      });
      return next;
    });
  };

  /** Validate, upload pending files, then POST registration + matching + documents. */
  const handleSubmit = async () => {
    const isValid = validation.validateForm(
      studentValidationPayload(formData, isAdult, contactType, secondaryContact),
    );
    if (!isValid) return;

    if (hasPendingUploadFiles(pendingFiles)) {
      setIsUploading(true);
    }
    let uploadedUrls: Record<string, string> = {};
    try {
      uploadedUrls = await uploadPendingFiles(pendingFiles, (file) =>
        defaultUpload(file, companyId, ''),
      );
    } catch (err) {
      toast.error(`Erreur lors de l'upload : ${getErrorMessage(err)}`);
      setIsUploading(false);
      return;
    }

    const enrichedData = mergePendingUploads(formData, uploadedUrls);

    createMutation.mutate(
      {
        dto: buildCreateStudentDto(
          enrichedData,
          contactType,
          secondaryContact,
          isAdult,
          resolvedParentContact,
          defaultCountry,
        ),
        companyId,
      },
      {
        onSuccess: () => {
          toast.success('Élève créé avec succès');
          onSuccess?.();
        },
        onError: (err: unknown) => toast.error(getErrorMessage(err)),
      },
    );
    setIsUploading(false);
  };

  return (
    <CollapseReplaceForm
      formType="create"
      title="Créer un élève"
      subtitle="Renseignez les informations de l'élève"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Créer"
      isSubmitting={isUploading || createMutation.isPending}
      isValid={
        isCreateStudentFormComplete(formData, isAdult) &&
        (!isAdult ||
          isPrimaryContactFormValid({
            primaryExists: contactExists,
            isPrimaryChecking,
            identical: contactsAreIdentical,
          }))
      }
    >
      <StudentRegistrationSection
        formData={formData}
        onChange={handleChange}
        onBlur={handleBlur}
        validation={validation}
        levelOptions={levelOptions}
        levels={levels}
        contactType={contactType}
        setContactType={setContactType}
        secondaryContact={secondaryContact}
        setSecondaryContact={setSecondaryContact}
        isAdult={isAdult}
        parentOptions={parentOptions}
        isParentFetching={isParentFetching}
        onParentSearch={setParentSearch}
        mode="create"
        initialParentUserId={initialParentUserId}
        initialParentName={initialParentName}
        defaultCountry={defaultCountry}
        primaryContactExists={contactExists}
      />
      <StudentMatchingSection
        formData={formData}
        onChange={handleChange}
        programs={programs}
        subjectOptions={REMOTE_SUBJECT_OPTIONS}
        partnerOptions={partnerOptions}
        sportClubOptions={sportClubOptions}
        partnersError={partnersError ? getErrorMessage(partnersError) : undefined}
        sportClubsError={sportClubsError ? getErrorMessage(sportClubsError) : undefined}
        hasSportProgram={hasSportProgram}
      />
      <StudentWelcomeMeetingSection
        formData={formData}
        onChange={handleChange}
        userId=""
        companyId={companyId}
        mode="create"
        user={user}
      />
      <StudentDocumentsSection
        formData={formData}
        onChange={handleNestedChange}
        studentUserId=""
        companyId={companyId}
        onFileSelect={handleFileChange}
      />
    </CollapseReplaceForm>
  );
}
